// Shared date filter bar for the Reports v2 sub-routes. Supports two shapes:
//
//  - Single-date pick (Collection Day Sheet, Day Book, Aging snapshot)
//  - Range pick + preset chips (Foreman Commission — FY, Member Statement — FY)
//
// Designed to slot inline into the report sub-route's page header. The presets
// match the prototype's chip-rail visual style.

import type { ReactElement } from 'react';
import { current_language } from '../i18n/index.js';
import { offset_ymd, today_ymd_ist } from '../lib/format.js';

interface SingleDateProps {
  mode: 'single';
  value: string;
  on_change: (ymd: string) => void;
  presets?: boolean; // default true
}

interface FyProps {
  mode: 'fy';
  fy_label: string;
  on_change: (fy_label: string) => void;
  /** Years to offer back from current; default 4. */
  back?: number;
}

type Props = SingleDateProps | FyProps;

export function DateFilterBar(props: Props): ReactElement {
  const lang = current_language();
  const t = (ta: string, en: string): string => (lang === 'ta' ? ta : en);

  if (props.mode === 'fy') {
    const back = props.back ?? 4;
    const ist_now = new Date(Date.now() + 5.5 * 60 * 60 * 1000);
    const current_start = ist_now.getUTCMonth() >= 3 ? ist_now.getUTCFullYear() : ist_now.getUTCFullYear() - 1;
    const options: string[] = [];
    for (let i = 0; i < back; i++) {
      const start = current_start - i;
      options.push(`${start}-${String((start + 1) % 100).padStart(2, '0')}`);
    }
    return (
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          {t('நிதியாண்டு', 'FY')}
        </span>
        {options.map((fy) => (
          <button
            key={fy}
            type="button"
            className={'btn sm' + (fy === props.fy_label ? ' primary' : '')}
            onClick={() => props.on_change(fy)}
          >
            {fy}
          </button>
        ))}
      </div>
    );
  }

  const today = today_ymd_ist();
  const yesterday = offset_ymd(today, -1);
  const week_ago = offset_ymd(today, -7);
  const presets_enabled = props.presets !== false;

  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
      <input
        type="date"
        value={props.value}
        onChange={(e) => props.on_change(e.target.value)}
        max={today}
        style={{ border: '1px solid var(--line)', borderRadius: 4, padding: '6px 8px', fontSize: 13, fontFamily: 'inherit' }}
      />
      {presets_enabled && (
        <div style={{ display: 'flex', gap: 4 }}>
          <button type="button" className={'btn sm' + (props.value === today ? ' primary' : '')} onClick={() => props.on_change(today)}>
            {t('இன்று', 'Today')}
          </button>
          <button type="button" className={'btn sm' + (props.value === yesterday ? ' primary' : '')} onClick={() => props.on_change(yesterday)}>
            {t('நேற்று', 'Yesterday')}
          </button>
          <button type="button" className={'btn sm' + (props.value === week_ago ? ' primary' : '')} onClick={() => props.on_change(week_ago)}>
            {t('7 நாள் முன்', '7 days ago')}
          </button>
        </div>
      )}
    </div>
  );
}
